/**
 * VideoInstructor — Talking Portrait of the realistic Caucasian female
 * instructor with subtle "alive" micro-movements (breathing scale,
 * head sway, head nod while speaking, and natural eye blinks).
 *
 * Locked-in design (Path A, Iter 51)
 *   The user chose this baseline after rejecting:
 *     • Looped brand video (uncanny when the audio is from TTS)
 *     • SVG mouth viseme overlay (read as a "smudge patch" at the
 *       photo resolution — every paint we tried looked off)
 *     • Cartoon Rive lipsync (inconsistent with realistic photo)
 *
 *   The talking signal is carried by:
 *     1. Soft female TTS voice (useSpeechNarration, rate 0.82)
 *     2. Karaoke text highlighting in LiveClassroomPanel
 *     3. This portrait's micro-movements — the visual presence anchor
 *
 * 60fps target & zero CLS
 *   • Container has explicit `width × height` so reserved area is
 *     known before the image loads → no Cumulative Layout Shift.
 *   • All animated elements use `transform`/`opacity` only (compositor-
 *     accelerated). `will-change: transform` hints the browser to
 *     promote them to GPU layers.
 *   • <img> has decoding="async" + fetchpriority="high" so the photo
 *     paints quickly without blocking the main thread.
 *
 * Path C (mesh-sync) lives in a sibling component `MeshInstructor.jsx`
 * behind the /lab/mesh-instructor sandbox route — kept separate so this
 * production component stays simple and predictable.
 *
 * Props
 *   speaking : bool — true while TTS is active
 *   word?    : string — current spoken word (UNUSED here; reserved
 *              for future mesh-sync prop forwarding)
 *   size?    : number — px width (default 200; height = 1.25× width)
 */
import { useEffect, useState } from "react";

const POSTER_URL = "/brand/instructor-portrait.jpg?v=6";

// Eye coordinates (verified by direct pixel detection on the cropped
// 480×600 portrait). Used by the blink overlay only.
const LEFT_EYE_X = "43%";
const RIGHT_EYE_X = "58%";
const EYES_Y = "25%";

export default function VideoInstructor({ speaking, size = 200 }) {
  const [blink, setBlink] = useState(false);

  // Random natural blink — every 3-6s, ~110ms long. Always on so
  // she feels alive even when not speaking.
  useEffect(() => {
    let stop = false;
    let timer;
    const tick = () => {
      if (stop) return;
      const wait = 3000 + Math.random() * 3000;
      timer = setTimeout(() => {
        if (stop) return;
        setBlink(true);
        setTimeout(() => {
          if (stop) return;
          setBlink(false);
          tick();
        }, 110);
      }, wait);
    };
    tick();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, []);

  // Reserved-aspect container — explicit dimensions prevent CLS.
  const height = size * 1.25;

  return (
    <div
      className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0E1420] to-[#0B0E14]"
      style={{ width: size, height, contain: "layout paint", willChange: "transform" }}
      data-testid="video-instructor"
      data-speaking={speaking ? "true" : "false"}
    >
      {/* Outer wrapper — slow head-sway always running. */}
      <div
        className="absolute inset-0"
        style={{
          animation: "oak-sway 7s ease-in-out infinite",
          willChange: "transform",
        }}
      >
        {/* Inner wrapper — adds a faster head-nod while speaking.
            Layered on a separate element because two CSS animations
            on the same `transform` would override each other. */}
        <div
          className="absolute inset-0"
          style={{
            animation: speaking ? "oak-nod 2.4s ease-in-out infinite" : "none",
            willChange: speaking ? "transform" : "auto",
          }}
        >
          <img
            src={POSTER_URL}
            alt=""
            decoding="async"
            fetchpriority="high"
            className="absolute inset-0 w-full h-full object-cover select-none"
            draggable={false}
            style={{
              animation: speaking ? "oak-breathe 1.6s ease-in-out infinite" : "none",
              willChange: speaking ? "transform" : "auto",
            }}
          />

          {/* Blink overlays — small skin-tone strips covering each eye
              for ~110ms. Coords derived from pixel detection. */}
          <div
            className="absolute pointer-events-none transition-opacity duration-75"
            style={{
              left: LEFT_EYE_X,
              top: EYES_Y,
              width: "6%",
              height: "1.6%",
              transform: "translate(-50%, -50%)",
              background: "linear-gradient(to bottom, #E2C2A8, #C8A88E)",
              borderRadius: "999px",
              opacity: blink ? 0.92 : 0,
              willChange: "opacity",
            }}
          />
          <div
            className="absolute pointer-events-none transition-opacity duration-75"
            style={{
              left: RIGHT_EYE_X,
              top: EYES_Y,
              width: "6%",
              height: "1.6%",
              transform: "translate(-50%, -50%)",
              background: "linear-gradient(to bottom, #E2C2A8, #C8A88E)",
              borderRadius: "999px",
              opacity: blink ? 0.92 : 0,
              willChange: "opacity",
            }}
          />
        </div>
      </div>

      {/* Inline keyframes — kept scoped to the component so we don't
          bleed into the global stylesheet. */}
      <style>{`
        @keyframes oak-sway {
          0%, 100% { transform: rotate(-0.5deg) translateX(-2px); }
          50%      { transform: rotate(0.5deg)  translateX(2px); }
        }
        @keyframes oak-nod {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(1.2px); }
        }
        @keyframes oak-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.012); }
        }
      `}</style>
    </div>
  );
}
